const DISCOUNT_QUERY_PARAMETER = 'discount';
const META_ADS_FALLBACK_DISCOUNT_CODE = 'BIENVENUEMS';
const META_ADS_FALLBACK_EXPIRES_AT = '2026-05-26T00:00:00+02:00';
const TOAST_DURATION = 5500;

const MESSAGES = {
  en: {
    applied: 'code {code} applies to eligible\u00a0items.',
    close: 'Close',
  },
  fr: {
    applied: 'code {code} s’applique aux articles\u00a0éligibles.',
    close: 'Fermer',
  },
  de: {
    applied: 'Code {code} gilt für berechtigte\u00a0Artikel.',
    close: 'Schließen',
  },
  es: {
    applied: 'código {code} se aplica a artículos\u00a0elegibles.',
    close: 'Cerrar',
  },
  it: {
    applied: 'codice {code} si applica agli articoli\u00a0idonei.',
    close: 'Chiudi',
  },
  nl: {
    applied: 'code {code} geldt voor geschikte\u00a0artikelen.',
    close: 'Sluiten',
  },
  pt: {
    applied: 'código {code} aplica-se a itens\u00a0elegíveis.',
    close: 'Fechar',
  },
};

/**
 * Reads a localized text bundle for the current page language.
 *
 * @returns {{ applied: string, close: string }}
 */
function getMessages() {
  const locale = document.documentElement.lang || navigator.language || 'en';
  const language = locale.toLowerCase().split('-')[0];

  return MESSAGES[language] || MESSAGES.en;
}

/**
 * Replaces a message token with a discount code.
 *
 * @param {string} message
 * @param {string} code
 * @returns {string}
 */
function formatMessage(message, code) {
  return message.replace('{code}', code);
}

/**
 * Builds a storefront URL that respects Shopify's locale root when available.
 *
 * @param {string} path
 * @returns {string}
 */
function getStorefrontUrl(path) {
  const root = window.Shopify?.routes?.root || '/';
  const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;

  return `${normalizedRoot}${normalizedPath}`;
}

/**
 * Finds a discount code entry in Shopify's cart JSON.
 *
 * @param {{ discount_codes?: Array<{ code: string }> }} cart
 * @param {string} code
 * @returns {{ code: string } | undefined}
 */
function findDiscountCode(cart, code) {
  const normalizedCode = code.toLowerCase();

  return cart.discount_codes?.find((discountCode) => discountCode.code.toLowerCase() === normalizedCode);
}

/**
 * Checks whether the current visit appears to come from Meta ads.
 *
 * @returns {boolean}
 */
function isMetaAdsVisit() {
  const params = new URLSearchParams(window.location.search);

  if (params.has('fbclid')) return true;

  const metaPattern = /(^|[^a-z0-9])(facebook|fb|instagram|ig|meta)([^a-z0-9]|$)/i;
  const attributionKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'source'];
  const hasMetaParameter = attributionKeys.some((key) => metaPattern.test(params.get(key) || ''));

  if (hasMetaParameter) return true;

  try {
    const referrerHost = document.referrer ? new URL(document.referrer).hostname : '';

    return /(^|\.)facebook\.com$|(^|\.)instagram\.com$|(^|\.)fb\.com$/i.test(referrerHost);
  } catch (error) {
    return false;
  }
}

/**
 * Checks whether the temporary Meta ads fallback is still active.
 *
 * @returns {boolean}
 */
function isMetaAdsFallbackActive() {
  return Date.now() < Date.parse(META_ADS_FALLBACK_EXPIRES_AT);
}

/**
 * Gets the discount code that should be linked to the cart for this visit.
 *
 * @returns {{ code: string, source: 'url_parameter' | 'meta_ads_fallback' } | undefined}
 */
function getDiscountContext() {
  const params = new URLSearchParams(window.location.search);
  const urlCode = params.get(DISCOUNT_QUERY_PARAMETER)?.trim();

  if (urlCode) {
    return { code: urlCode, source: 'url_parameter' };
  }

  if (isMetaAdsFallbackActive() && isMetaAdsVisit()) {
    return { code: META_ADS_FALLBACK_DISCOUNT_CODE, source: 'meta_ads_fallback' };
  }

  return undefined;
}

/**
 * Tracks a PostHog event without affecting the discount flow.
 *
 * @param {string} eventName
 * @param {Record<string, unknown>} properties
 * @returns {void}
 */
function safeCapturePostHogEvent(eventName, properties) {
  try {
    window.posthog?.capture?.(eventName, properties);
  } catch (error) {
    // Analytics must never block storefront behavior.
  }
}

/**
 * Applies a discount code from the current URL and shows an accessible toast.
 */
class UrlDiscountToast extends HTMLElement {
  #dismissTimeout;
  #messages = getMessages();

  connectedCallback() {
    this.hidden = true;
    this.attachShadow({ mode: 'open' });
    this.#render();
    this.#applyDiscountFromContext();
  }

  disconnectedCallback() {
    window.clearTimeout(this.#dismissTimeout);
  }

  /**
   * Handles the full URL discount flow.
   *
   * @returns {Promise<void>}
   */
  async #applyDiscountFromContext() {
    const discountContext = getDiscountContext();

    if (!discountContext) return;

    const { code, source } = discountContext;
    try {
      const initialCart = await this.#fetchCart();
      const initialDiscount = findDiscountCode(initialCart, code);

      if (initialDiscount) {
        return;
      }
    } catch (error) {
      // If the initial cart read fails, still try Shopify's discount URL.
    }

    try {
      await fetch(getStorefrontUrl(`discount/${encodeURIComponent(code)}`), {
        method: 'GET',
        credentials: 'same-origin',
      });

      this.#showToast('applied', code);
      safeCapturePostHogEvent('discount_auto_apply', {
        discount_code: code,
        discount_source: source,
        page_path: window.location.pathname,
        page_url: window.location.href,
      });
    } catch (error) {
      // Do not expose discount-linking failures; Shopify intentionally avoids discount-code validation leaks here.
    }
  }

  /**
   * Fetches Shopify's cart JSON.
   *
   * @returns {Promise<{ discount_codes?: Array<{ code: string }> }>}
   */
  async #fetchCart() {
    const response = await fetch(getStorefrontUrl('cart.json'), {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Unable to fetch cart: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Shows the toast with a localized status message.
   *
   * @param {'applied'} status
   * @param {string} code
   * @returns {void}
   */
  #showToast(status, code) {
    const toast = this.shadowRoot?.querySelector('[data-toast]');
    const message = this.shadowRoot?.querySelector('[data-message]');

    if (!toast || !message) return;

    toast.dataset.status = status;
    toast.style.setProperty('--toast-duration', `${TOAST_DURATION}ms`);
    message.textContent = formatMessage(this.#messages[status], code);
    this.hidden = false;

    window.clearTimeout(this.#dismissTimeout);
    this.#dismissTimeout = window.setTimeout(() => this.#hideToast(), TOAST_DURATION);
  }

  /**
   * Hides the toast.
   *
   * @returns {void}
   */
  #hideToast() {
    this.hidden = true;
  }

  /**
   * Renders the component shadow DOM.
   *
   * @returns {void}
   */
  #render() {
    if (!this.shadowRoot) return;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          position: fixed;
          z-index: 2147483647;
          right: max(16px, env(safe-area-inset-right));
          bottom: max(16px, env(safe-area-inset-bottom));
          width: min(360px, calc(100vw - 32px));
          font: inherit;
        }

        :host([hidden]) {
          display: none;
        }

        .toast {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 12px;
          align-items: center;
          position: relative;
          overflow: hidden;
          padding: 14px 14px 14px 16px;
          color: rgb(var(--color-foreground-rgb, 18 18 18));
          background: rgb(var(--color-background-rgb, 255 255 255));
          border: 1px solid rgb(var(--color-border-rgb, 230 230 230));
          border-radius: min(var(--style-border-radius-buttons-primary, 6px), 8px);
          box-shadow: 0 12px 32px rgb(0 0 0 / 0.16);
        }

        .toast::after {
          content: '';
          position: absolute;
          right: 0;
          bottom: 0;
          left: 0;
          height: 3px;
          background: #fff;
          transform-origin: left center;
          animation: toast-progress var(--toast-duration, 9000ms) linear forwards;
        }

        .message {
          margin: 0;
          font-size: 0.95rem;
          line-height: 1.35;
        }

        .close {
          display: grid;
          place-items: center;
          width: 32px;
          height: 32px;
          min-width: 32px;
          min-height: 32px;
          padding: 0;
          color: currentColor;
          background: transparent;
          border: 0;
          border-radius: 4px;
          cursor: pointer;
        }

        .close:hover {
          background: rgb(var(--color-foreground-rgb, 18 18 18) / 0.08);
        }

        @media screen and (max-width: 749px) {
          :host {
            right: auto;
            left: 50%;
            transform: translateX(-50%);
            width: min(360px, calc(100vw - 24px));
          }

          .toast {
            grid-template-columns: 1fr auto;
          }

          .message {
            text-align: center;
          }
        }

        @keyframes toast-progress {
          from {
            transform: scaleX(1);
          }

          to {
            transform: scaleX(0);
          }
        }
      </style>
      <div
        class="toast"
        data-toast
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <p class="message" data-message></p>
        <button class="close" type="button" aria-label="${this.#messages.close}" data-close>
          <svg aria-hidden="true" viewBox="0 0 16 16" width="16" height="16">
            <path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"></path>
          </svg>
        </button>
      </div>
    `;

    this.shadowRoot.querySelector('[data-close]')?.addEventListener('click', () => this.#hideToast());
  }
}

if (!customElements.get('url-discount-toast')) {
  customElements.define('url-discount-toast', UrlDiscountToast);
}

/**
 * Mounts the URL discount toast component once.
 *
 * @returns {void}
 */
function mountUrlDiscountToast() {
  if (!document.querySelector('url-discount-toast')) {
    document.body.append(document.createElement('url-discount-toast'));
  }
}

if (getDiscountContext()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountUrlDiscountToast, { once: true });
  } else {
    mountUrlDiscountToast();
  }
}
